import "./index.css";
import { clsx } from "clsx";
import { Trash2Icon, XIcon } from "lucide-react";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { INotification } from "@/types/notifications";
import { IconButton, Text, useDateLocale } from "@/ui-kit";
import { serverErrorHandler } from "@/utils/serverErrorHandler";
import { notificationsCenter } from "../../model";
import { formatNotificationDate, toAppPath } from "../../utils";

type IProps = {
  item: INotification;
  isMobile: boolean;
  onNavigate: (path: string) => void;
};

const NotificationItemComponent = ({ item, isMobile, onNavigate }: IProps) => {
  const { t } = useTranslation();
  const { displayLocale } = useDateLocale();
  const textSize = isMobile ? "lg" : "md";

  const handleOpen = useCallback(() => {
    notificationsCenter.markAsRead(item.id).catch(serverErrorHandler());

    const path = toAppPath(item.link);
    if (path) {
      onNavigate(path);
    }
  }, [item.id, item.link, onNavigate]);

  const handleDelete = useCallback(() => {
    notificationsCenter.remove(item.id).catch(serverErrorHandler());
  }, [item.id]);

  return (
    <li className="notification">
      <button type="button" className="notification__main" onClick={handleOpen}>
        <span
          aria-hidden="true"
          className={clsx(
            "notification__marker nuco-color-brand",
            item.isRead && "notification__marker--read",
          )}
        />
        <span className="notification__details">
          {!item.isRead && (
            <span className="notification__sr-only">{t("label.unread")}</span>
          )}
          {item.title && (
            <Text size={textSize} weight="bold" className="notification__title">
              {item.title}
            </Text>
          )}
          {item.message && (
            <Text size={textSize} className="notification__text">
              {item.message}
            </Text>
          )}
          <Text size={textSize} className="notification__time">
            <time dateTime={item.createdOn}>
              {formatNotificationDate(
                item.createdOn,
                displayLocale,
                t("label.yesterday"),
              )}
            </time>
          </Text>
        </span>
      </button>
      <IconButton
        className="notification__delete"
        size="sm"
        color="neutral"
        variant="ghost"
        aria-label={t("action.deleteNotification")}
        onClick={handleDelete}
      >
        {isMobile ? <Trash2Icon /> : <XIcon />}
      </IconButton>
    </li>
  );
};

// Rows re-render only when their own item changes: actions replace one item, not the list
export const NotificationItem = memo(NotificationItemComponent);
